import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
    ArrowRight,
    Clapperboard,
    HandCoins,
    LineChart,
    Zap,
    Library,
    Plug,
    Globe2,
    Infinity as InfinityIcon,
    ShieldCheck,
    Sparkles,
    Play,
    Filter,
    Calendar,
    ScrollText,
    FileCheck,
} from "lucide-react";

/* ---------- COUNT-UP HOOK -------------------------------------------- */

function useCountUp(target, durationMs = 1200, deps = []) {
    const [val, setVal] = React.useState(0);
    React.useEffect(() => {
        let start;
        let raf;
        const tick = (t) => {
            if (!start) start = t;
            const p = Math.min(1, (t - start) / durationMs);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(target * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [target, durationMs, ...deps]);
    return val;
}

/* ---------- LIVE SPARKLINE ------------------------------------------- */

function Sparkline({ color = "#FF5A5F", points = [4, 7, 5, 9, 6, 11, 8, 13, 10, 14] }) {
    const w = 64;
    const h = 18;
    const max = Math.max(...points);
    const step = w / (points.length - 1);
    const path = points
        .map((p, i) => `${i === 0 ? "M" : "L"}${i * step},${h - (p / max) * h}`)
        .join(" ");
    return (
        <svg width={w} height={h} className="overflow-visible">
            <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <circle
                cx={w}
                cy={h - (points[points.length - 1] / max) * h}
                r="2.5"
                fill={color}
            >
                <animate attributeName="r" values="2.5;4;2.5" dur="1.4s" repeatCount="indefinite" />
            </circle>
        </svg>
    );
}

/* ---------- MODE CONFIG ----------------------------------------------- */

const BRAND = {
    mode: "brand",
    chip: "FOR BRANDS",
    h1Line1: "Take your winning videos.",
    h1Accent: "License instantly. Scale everywhere.",
    sub: "Get creator content. Use it your way. Pay on performance. Download raw videos to run on Meta, Google, Amazon, YouTube and more — Castline tracks revenue and pays creators automatically.",
    ctaLabel: "Add my brand",
    ctaHref: "/register?role=brand",
    usps: [
        { Icon: ScrollText, title: "License in 1 click, not 1 month", desc: "Skip the legal back-and-forth. Get the rights to use creator videos as paid ads — across every channel — the moment they accept your invite." },
        { Icon: Clapperboard, title: "Use it your way", desc: "Download the raw .mp4 to remix, mash up or run as-is on Meta, Google, Amazon, YouTube — every channel you spend on." },
        { Icon: HandCoins, title: "Pay only on performance", desc: "Only pay when content drives revenue. Flat fee or rev-share — your call, per video, per creator, or in bulk." },
        { Icon: LineChart, title: "See which video drives revenue", desc: "Per-channel ROAS, per-creator earnings, per-video lift. One dashboard, no spreadsheets." },
        { Icon: Library, title: "Build a reusable ad library", desc: "Winning creative becomes inventory you can run again and again — not a one-time deliverable." },
        { Icon: Plug, title: "Connect ad accounts in one click", desc: "Plug in Meta, Google, Amazon, YouTube, Pinterest and Snap. We sync spend, attribute revenue, and pay creators automatically." },
    ],
    narrativeH: "Keep making content.",
    narrativeAccent: "Make every winner work everywhere.",
    narrativeSub: "Take your winning videos and scale them everywhere.",
    steps: [
        { n: "01", title: "Invite your creators", desc: "Bring the partners you already work with onto Castline." },
        { n: "02", title: "Download & run", desc: "Grab raw videos plus the UTM, promo code and campaign tag. Run on Meta, Google, Amazon — your way." },
        { n: "03", title: "Pay on performance", desc: "Set flat or rev-share terms per video, creator or in bulk. Castline tracks revenue and pays automatically." },
    ],
    finalH: "One library. Every channel. Every creator.",
    finalSub: "Free to set up. You only pay when content runs.",
};

const CREATOR = {
    mode: "creator",
    chip: "FOR CREATORS",
    h1Line1: "Post once.",
    h1Accent: "Earn everywhere it performs.",
    sub: "Your best videos stop being one-time deliverables. Brands run them on Meta, Google, Amazon and more — and you earn every time they drive revenue.",
    ctaLabel: "Become a creator",
    ctaHref: "/register?role=creator",
    usps: [
        { Icon: Globe2, title: "Earn beyond where you post", desc: "Your videos can run as ads on any channel a brand uses. Same content, more channels, more payouts." },
        { Icon: InfinityIcon, title: "Get paid when it performs", desc: "Earn an ongoing share of the revenue your video drives — for as long as the brand keeps running it." },
        { Icon: ShieldCheck, title: "No silent rate cuts", desc: "Rate increases land instantly. Any decrease needs your opt-in. Past payouts are never repriced." },
        { Icon: FileCheck, title: "No legal back-and-forth", desc: "Clear terms, no jargon. No multi-page contracts, no asking for permission every time the brand wants to run it again." },
        { Icon: LineChart, title: "See what's earning", desc: "Real-time dashboard: which videos, which channels, which brands. No black box." },
        { Icon: Zap, title: "One library, many brands", desc: "One creator, multiple brands. Upload once, earn from every brand that runs it." },
    ],
    narrativeH: "Stop posting and forgetting.",
    narrativeAccent: "Start earning when it performs.",
    narrativeSub: "Your best videos aren't one-time deliverables. They're assets that keep paying as they drive results.",
    steps: [
        { n: "01", title: "Get invited", desc: "Brands you already work with bring you in." },
        { n: "02", title: "Upload once", desc: "Your videos become reusable ad inventory." },
        { n: "03", title: "Earn everywhere", desc: "Commission on every channel, every month." },
    ],
    finalH: "Royalties for the creator economy.",
    finalSub: "Free to join. Get paid every time your content performs.",
};

/* ---------- CHANNEL ROW ----------------------------------------------- */

const channels = ["META", "GOOGLE", "YOUTUBE", "AMAZON", "PINTEREST", "SNAPCHAT", "LINKEDIN", "X", "REDDIT"];

/* ---------- MODE TOGGLE ---------------------------------------------- */

function ModeToggle({ mode, setMode }) {
    return (
        <div
            className="inline-flex items-center p-1 rounded-full border hairline bg-white shadow-sm"
            data-testid="landing-mode-toggle"
        >
            {[
                { v: "brand", label: "I'm a Brand", activeCls: "bg-coral text-white" },
                { v: "creator", label: "I'm a Creator", activeCls: "bg-lime text-lime-ink" },
            ].map((o) => (
                <button
                    key={o.v}
                    onClick={() => setMode(o.v)}
                    data-testid={`mode-toggle-${o.v}`}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                        mode === o.v
                            ? `${o.activeCls} shadow-sm`
                            : "text-slate-500 hover:text-slate-900"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

/* ---------- VIDEO COLLAGE (narrative section) ------------------------ */

const videoThumbs = [
    "https://images.pexels.com/photos/8111311/pexels-photo-8111311.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=600",
    "https://images.pexels.com/photos/7691673/pexels-photo-7691673.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=600",
    "https://images.pexels.com/photos/3761509/pexels-photo-3761509.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=600",
];

function VideoCollage({ mode }) {
    const floaters =
        mode === "brand"
            ? [
                  { top: "8%", right: "-14px", label: "Running on META", accent: "chip-coral" },
                  { bottom: "14%", left: "-20px", label: "2.7x ROAS", accent: "chip-lime" },
                  { bottom: "-10px", right: "10%", label: "12 active ads", accent: "chip" },
              ]
            : [
                  { top: "8%", right: "-14px", label: "+$560 earned", accent: "chip-coral" },
                  { bottom: "14%", left: "-20px", label: "Running on META", accent: "chip-lime" },
                  { bottom: "-10px", right: "10%", label: "3 brands paying", accent: "chip" },
              ];

    return (
        <div className="relative group" data-testid={`video-collage-${mode}`}>
            <div className="grid grid-cols-12 gap-3 relative">
                {/* back card */}
                <div className="col-span-5 col-start-1 row-start-1 mt-16 card-ecom overflow-hidden aspect-[3/4] rotate-[-4deg] transition-transform duration-500 ease-out group-hover:rotate-0 group-hover:-translate-y-1">
                    <img src={videoThumbs[1]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                {/* main card */}
                <div className="col-span-7 col-start-4 row-start-1 card-ecom overflow-hidden aspect-[3/4] shadow-xl relative transition-transform duration-500 ease-out group-hover:scale-[1.02] group-hover:-translate-y-2">
                    <img src={videoThumbs[0]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                            <Play className="w-6 h-6 text-slate-900 fill-slate-900 ml-0.5" />
                        </div>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                        <span className="chip bg-white/95 backdrop-blur text-[10px]">00:15</span>
                        <span className="chip chip-lime text-[10px]">UGC</span>
                    </div>
                </div>
                {/* front/side card */}
                <div className="col-span-4 col-start-9 row-start-1 mt-24 card-ecom overflow-hidden aspect-[3/4] rotate-[6deg] hidden sm:block transition-transform duration-500 ease-out group-hover:rotate-0 group-hover:-translate-y-1">
                    <img src={videoThumbs[2]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
            </div>

            {/* Floating chips */}
            {floaters.map((f, i) => (
                <div
                    key={i}
                    className="absolute card-ecom bg-white px-3 py-2 shadow-lg flex items-center gap-2"
                    style={{
                        top: f.top,
                        right: f.right,
                        bottom: f.bottom,
                        left: f.left,
                    }}
                >
                    <span
                        className={`inline-block w-1.5 h-1.5 rounded-full ${
                            f.accent === "chip-coral"
                                ? "bg-coral"
                                : f.accent === "chip-lime"
                                  ? "bg-lime-ink"
                                  : "bg-slate-400"
                        }`}
                    />
                    <span className="text-xs font-semibold text-slate-900">{f.label}</span>
                </div>
            ))}
        </div>
    );
}

/* ---------- BRAND DASHBOARD MOCK ------------------------------------- */

function BrandDashboardMock() {
    const revenue = useCountUp(15285, 1500);
    const roas = useCountUp(2.83, 1500);
    const rows = [
        { name: "Spring_drop_hero", meta: "1 video · flat", channels: ["Meta", "Google"], spend: "$300", rev: "$855", paid: "$128" },
        { name: "Loungewear_unbox", meta: "1 video · flat", channels: ["Meta", "Google"], spend: "$300", rev: "$855", paid: "$128" },
        { name: "Commission · TEST_e5d092", meta: "1 video · rev-share", channels: ["Meta", "Google"], spend: "$450", rev: "$1,282", paid: "$192" },
        { name: "Protein_shaker_demo", meta: "1 video · flat", channels: ["Amazon"], spend: "$300", rev: "$855", paid: "$128" },
    ];
    return (
        <div className="card-ecom p-3 sm:p-5 bg-white" data-testid="brand-dashboard-mock">
            {/* Stat cards — 2-col mobile, 3 sm, 5 lg */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
                <div className="rounded-xl border hairline p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Total spend</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-slate-900">$5,400</div>
                </div>
                <div className="rounded-xl border hairline p-2.5 relative overflow-hidden">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Revenue</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-coral tabular-nums">
                        ${Math.round(revenue).toLocaleString()}
                    </div>
                    <div className="absolute right-1.5 bottom-1.5 opacity-80 hidden sm:block">
                        <Sparkline color="#FF5A5F" />
                    </div>
                </div>
                <div className="rounded-xl border hairline p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Creators paid</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-slate-900">$576</div>
                </div>
                <div className="rounded-xl border hairline p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Brand kept</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-slate-900">$14,709</div>
                </div>
                <div className="rounded-xl border hairline p-2.5 col-span-2 sm:col-span-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">ROAS</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-lime-ink tabular-nums">
                        {roas.toFixed(2)}x
                    </div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="rounded-xl border hairline p-2 sm:p-2.5 flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
                <div className="flex items-center gap-1.5 text-slate-500">
                    <Filter className="w-3 h-3" />
                    <span className="text-[10px] uppercase tracking-wider">Filters</span>
                </div>
                <div className="px-2.5 py-1 rounded-full border hairline text-[11px] sm:text-xs text-slate-600 bg-white">
                    All creators
                </div>
                <div className="px-2.5 py-1 rounded-full border hairline text-[11px] sm:text-xs text-slate-600 bg-white">
                    All channels
                </div>
                <span className="ml-auto chip chip-lime text-[10px] inline-flex items-center gap-1.5">
                    <span className="relative flex w-1.5 h-1.5">
                        <span className="absolute inset-0 rounded-full bg-lime-ink opacity-60 animate-ping" />
                        <span className="relative w-1.5 h-1.5 rounded-full bg-lime-ink" />
                    </span>
                    LIVE
                </span>
            </div>

            {/* Sub-tabs — scrollable on mobile */}
            <div className="overflow-x-auto -mx-1 px-1 mb-3 no-scrollbar">
                <div className="inline-flex p-1 rounded-full bg-slate-100">
                    {[
                        { label: "Campaigns", active: true },
                        { label: "Videos" },
                        { label: "Creators" },
                        { label: "Channels" },
                    ].map((t) => (
                        <span
                            key={t.label}
                            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                                t.active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                            }`}
                        >
                            {t.label}
                        </span>
                    ))}
                </div>
            </div>

            {/* Mobile stacked rows */}
            <div className="space-y-2 md:hidden">
                {rows.map((r) => (
                    <div key={r.name} className="rounded-xl border hairline p-3">
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                                <div className="font-semibold text-slate-900 text-sm truncate">{r.name}</div>
                                <div className="text-[11px] text-slate-400 truncate">{r.meta}</div>
                            </div>
                            <div className="flex flex-wrap gap-1 flex-shrink-0 justify-end">
                                {r.channels.map((c) => (
                                    <span key={c} className="chip chip-lime text-[10px] py-0.5 px-2">
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t hairline">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Spend</div>
                                <div className="text-sm font-semibold text-slate-900">{r.spend}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Revenue</div>
                                <div className="text-sm font-semibold text-coral">{r.rev}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Paid</div>
                                <div className="text-sm font-semibold text-slate-900">{r.paid}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl border hairline overflow-hidden">
                <div className="grid grid-cols-[1.6fr_1fr_0.6fr_0.7fr_0.7fr] px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <div>Campaign</div>
                    <div>Channels</div>
                    <div className="text-right">Spend</div>
                    <div className="text-right">Revenue</div>
                    <div className="text-right">Creator paid</div>
                </div>
                {rows.map((r, i) => (
                    <div
                        key={r.name}
                        className={`grid grid-cols-[1.6fr_1fr_0.6fr_0.7fr_0.7fr] items-center px-3 py-2.5 text-xs ${
                            i !== rows.length - 1 ? "border-b hairline" : ""
                        }`}
                    >
                        <div className="min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                            <div className="text-[10px] text-slate-400 truncate">{r.meta}</div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {r.channels.map((c) => (
                                <span key={c} className="chip chip-lime text-[10px] py-0.5 px-2">
                                    {c}
                                </span>
                            ))}
                        </div>
                        <div className="text-right text-slate-700">{r.spend}</div>
                        <div className="text-right font-semibold text-coral">{r.rev}</div>
                        <div className="text-right text-slate-700">{r.paid}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------- CREATOR DASHBOARD MOCK ----------------------------------- */

function CreatorDashboardMock() {
    const earnings = useCountUp(3240, 1500);
    const topRoas = useCountUp(3.5, 1500);
    const rows = [
        { name: "Spring_drop_hero", meta: "Meta · Glossier", spend: "$300", rev: "$11.2k", earned: "+$560" },
        { name: "Loungewear_unbox", meta: "Google · Vuori", spend: "$2.8k", rev: "$8.7k", earned: "+$435" },
        { name: "Protein_shaker_demo", meta: "Amazon · MuscleLab", spend: "$3.1k", rev: "$10.9k", earned: "+$545" },
        { name: "Morning_routine_GRWM", meta: "YouTube · Allbirds", spend: "$1.9k", rev: "$4.6k", earned: "+$230" },
    ];
    return (
        <div className="card-ecom p-3 sm:p-5 bg-white" data-testid="creator-dashboard-mock">
            {/* Stat cards — 2-col mobile, 3 sm, 5 lg */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
                <div className="rounded-xl border hairline p-2.5 relative overflow-hidden">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Earnings</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-coral tabular-nums">
                        ${Math.round(earnings).toLocaleString()}
                    </div>
                    <div className="absolute right-1.5 bottom-1.5 opacity-80 hidden sm:block">
                        <Sparkline color="#FF5A5F" />
                    </div>
                </div>
                <div className="rounded-xl border hairline p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Videos</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-slate-900">4</div>
                </div>
                <div className="rounded-xl border hairline p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Brands</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-slate-900">4</div>
                </div>
                <div className="rounded-xl border hairline p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Channels</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-slate-900">4</div>
                </div>
                <div className="rounded-xl border hairline p-2.5 col-span-2 sm:col-span-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Top ROAS</div>
                    <div className="font-display text-base sm:text-lg font-semibold mt-0.5 text-lime-ink tabular-nums">
                        {topRoas.toFixed(1)}x
                    </div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="rounded-xl border hairline p-2 sm:p-2.5 flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
                <div className="flex items-center gap-1.5 text-slate-500">
                    <Filter className="w-3 h-3" />
                    <span className="text-[10px] uppercase tracking-wider">Filters</span>
                </div>
                <div className="px-2.5 py-1 rounded-full border hairline text-[11px] sm:text-xs text-slate-600 bg-white">
                    All brands
                </div>
                <div className="px-2.5 py-1 rounded-full border hairline text-[11px] sm:text-xs text-slate-600 bg-white">
                    All channels
                </div>
                <span className="ml-auto chip chip-lime text-[10px] inline-flex items-center gap-1.5">
                    <span className="relative flex w-1.5 h-1.5">
                        <span className="absolute inset-0 rounded-full bg-lime-ink opacity-60 animate-ping" />
                        <span className="relative w-1.5 h-1.5 rounded-full bg-lime-ink" />
                    </span>
                    LIVE
                </span>
            </div>

            {/* Sub-tabs — scrollable on mobile */}
            <div className="overflow-x-auto -mx-1 px-1 mb-3 no-scrollbar">
                <div className="inline-flex p-1 rounded-full bg-slate-100">
                    {[
                        { label: "Videos", active: true },
                        { label: "Brands" },
                        { label: "Channels" },
                        { label: "Payouts" },
                    ].map((t) => (
                        <span
                            key={t.label}
                            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                                t.active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                            }`}
                        >
                            {t.label}
                        </span>
                    ))}
                </div>
            </div>

            {/* Mobile stacked rows */}
            <div className="space-y-2 md:hidden">
                {rows.map((r) => (
                    <div key={r.name} className="rounded-xl border hairline p-3">
                        <div className="flex items-start gap-2 mb-2 min-w-0">
                            <div className="w-7 h-7 rounded-md bg-slate-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Play className="w-3 h-3 text-white fill-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="font-semibold text-slate-900 text-sm truncate">{r.name}</div>
                                <div className="text-[11px] text-slate-500 truncate">{r.meta}</div>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Earned</div>
                                <div className="text-sm font-semibold text-coral">{r.earned}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t hairline">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Spend</div>
                                <div className="text-sm font-semibold text-slate-900">{r.spend}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400">Drove</div>
                                <div className="text-sm font-semibold text-slate-900">{r.rev}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-xl border hairline overflow-hidden">
                <div className="grid grid-cols-[1.6fr_1.1fr_0.7fr_0.7fr_0.8fr] px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <div>Video</div>
                    <div>Channel · Brand</div>
                    <div className="text-right">Spend</div>
                    <div className="text-right">Drove</div>
                    <div className="text-right">You earned</div>
                </div>
                {rows.map((r, i) => (
                    <div
                        key={r.name}
                        className={`grid grid-cols-[1.6fr_1.1fr_0.7fr_0.7fr_0.8fr] items-center px-3 py-2.5 text-xs ${
                            i !== rows.length - 1 ? "border-b hairline" : ""
                        }`}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-6 h-6 rounded-md bg-slate-900 flex items-center justify-center flex-shrink-0">
                                <Play className="w-2.5 h-2.5 text-white fill-white" />
                            </div>
                            <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{r.meta}</div>
                        <div className="text-right text-slate-700">{r.spend}</div>
                        <div className="text-right text-slate-700">{r.rev}</div>
                        <div className="text-right font-semibold text-coral">{r.earned}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------- LANDING -------------------------------------------------- */

export default function Landing() {
    const [params, setParams] = useSearchParams();
    const mode = params.get("mode") === "creator" ? "creator" : "brand";
    const cfg = mode === "creator" ? CREATOR : BRAND;

    const setMode = (next) => {
        if (next === "brand") {
            params.delete("mode");
        } else {
            params.set("mode", "creator");
        }
        setParams(params, { replace: true });
        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const DashboardMock = mode === "creator" ? CreatorDashboardMock : BrandDashboardMock;

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <Navbar />

            {/* MODE TOGGLE STRIP */}
            <div className="border-b hairline bg-white">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-4 flex justify-center">
                    <ModeToggle mode={mode} setMode={setMode} />
                </div>
            </div>

            {/* HERO */}
            <section
                key={`hero-${mode}`}
                className="relative overflow-hidden fade-up"
                data-testid={`landing-hero-${mode}`}
            >
                <div className="absolute inset-0 -z-10">
                    <div
                        className={`absolute top-10 -left-20 w-96 h-96 rounded-full blur-3xl ${
                            mode === "brand" ? "bg-coral/10" : "bg-lime/25"
                        }`}
                    />
                    <div
                        className={`absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-3xl ${
                            mode === "brand" ? "bg-lime/25" : "bg-coral/15"
                        }`}
                    />
                </div>
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-12 lg:py-24 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                    <div className="lg:col-span-5">
                        <div
                            className={`inline-flex items-center gap-2 mb-5 sm:mb-6 ${
                                mode === "brand" ? "chip chip-coral" : "chip chip-lime"
                            }`}
                        >
                            <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                    mode === "brand" ? "bg-coral" : "bg-lime-ink"
                                }`}
                            />
                            {cfg.chip}
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95] text-slate-900">
                            {cfg.h1Line1}
                            <br />
                            <span className="relative inline-block">
                                <span className="relative z-10">{cfg.h1Accent}</span>
                                <span
                                    className={`absolute inset-x-0 bottom-1 h-3 -z-0 rounded-sm ${
                                        mode === "brand" ? "bg-lime/70" : "bg-coral/30"
                                    }`}
                                />
                            </span>
                        </h1>
                        <p className="mt-5 sm:mt-7 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                            {cfg.sub}
                        </p>
                        <div className="mt-7 sm:mt-9 flex flex-wrap gap-3">
                            {cfg.ctaHref.startsWith("mailto:") ? (
                                <a href={cfg.ctaHref}>
                                    <button
                                        className="btn-pill-primary inline-flex items-center"
                                        data-testid={`hero-cta-${mode}`}
                                    >
                                        {cfg.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
                                    </button>
                                </a>
                            ) : (
                                <Link to={cfg.ctaHref}>
                                    <button
                                        className="btn-pill-primary inline-flex items-center"
                                        data-testid={`hero-cta-${mode}`}
                                    >
                                        {cfg.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
                                    </button>
                                </Link>
                            )}
                            <a href="#usps">
                                <button
                                    className="btn-pill-ghost inline-flex items-center"
                                    data-testid="hero-cta-learn"
                                >
                                    Learn more
                                </button>
                            </a>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <DashboardMock />
                    </div>
                </div>

                {/* Channel strip */}
                <div className="border-y hairline py-4 overflow-hidden bg-white">
                    <div className="flex marquee-track whitespace-nowrap">
                        {[...channels, ...channels, ...channels].map((c, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-10 px-10 overline text-slate-400"
                            >
                                {c}
                                <span
                                    className={`w-1 h-1 rounded-full ${
                                        mode === "brand" ? "bg-coral" : "bg-lime-ink"
                                    }`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* USP GRID */}
            <section id="usps" className="py-16 lg:py-24" data-testid={`usps-${mode}`}>
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
                    <div className="max-w-3xl mb-10 sm:mb-14">
                        <div
                            className={`inline-flex mb-4 ${
                                mode === "brand" ? "chip chip-coral" : "chip chip-lime"
                            }`}
                        >
                            WHY CASTLINE
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
                            {mode === "brand"
                                ? "The simple system for scaling creator content."
                                : "The simple system for earning on every channel."}
                        </h2>
                    </div>
                    <div
                        className={`grid gap-5 ${
                            cfg.usps.length === 6
                                ? "md:grid-cols-2 lg:grid-cols-3"
                                : "md:grid-cols-2 lg:grid-cols-3"
                        }`}
                    >
                        {cfg.usps.map(({ Icon, title, desc }, idx) => (
                            <div
                                key={title}
                                className="card-ecom p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                                data-testid={`usp-${mode}-${idx}`}
                            >
                                <div
                                    className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${
                                        idx % 2 === 0
                                            ? "bg-coral/10 text-coral"
                                            : "bg-lime/40 text-lime-ink"
                                    }`}
                                >
                                    <Icon className="w-7 h-7" strokeWidth={1.75} />
                                </div>
                                <h3 className="font-display text-xl font-semibold mb-2 text-slate-900">
                                    {title}
                                </h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NARRATIVE + VIDEO COLLAGE */}
            <section className="py-16 lg:py-24 surface-warm overflow-hidden" data-testid={`narrative-${mode}`}>
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <div className="lg:col-span-5">
                        <div
                            className={`inline-flex mb-4 ${
                                mode === "brand" ? "chip chip-coral" : "chip chip-lime"
                            }`}
                        >
                            A NEW MODEL
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-4 sm:mb-5 leading-[1.05]">
                            {cfg.narrativeH}{" "}
                            <span className="text-coral">
                                {cfg.narrativeAccent}
                            </span>
                        </h2>
                        <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-6 sm:mb-7 max-w-lg">
                            {cfg.narrativeSub}
                        </p>
                        {cfg.ctaHref.startsWith("mailto:") ? (
                            <a href={cfg.ctaHref}>
                                <button
                                    className="btn-pill-primary inline-flex items-center"
                                    data-testid={`narrative-cta-${mode}`}
                                >
                                    {cfg.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
                                </button>
                            </a>
                        ) : (
                            <Link to={cfg.ctaHref}>
                                <button
                                    className="btn-pill-primary inline-flex items-center"
                                    data-testid={`narrative-cta-${mode}`}
                                >
                                    {cfg.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
                                </button>
                            </Link>
                        )}
                    </div>
                    <div className="lg:col-span-7 px-4 sm:px-0">
                        <VideoCollage mode={mode} />
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="py-16 lg:py-28" data-testid={`how-${mode}`}>
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                        <div
                            className={`inline-flex mb-4 ${
                                mode === "brand" ? "chip chip-coral" : "chip chip-lime"
                            }`}
                        >
                            HOW IT WORKS
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
                            {mode === "brand"
                                ? "Three steps to your creator ad engine."
                                : "Three steps to ongoing royalties."}
                        </h2>
                    </div>
                    <div className="relative grid md:grid-cols-3 gap-10 md:gap-6">
                        {/* dashed connector — desktop only */}
                        <div className="hidden md:block absolute top-12 left-[18%] right-[18%] h-0.5 border-t-2 border-dashed border-slate-200 -z-0" />

                        {cfg.steps.map(({ n, title, desc }, idx) => (
                            <div
                                key={n}
                                className="relative flex flex-col items-center text-center md:items-start md:text-left"
                                data-testid={`step-${mode}-${n}`}
                            >
                                {/* Giant number circle */}
                                <div className="relative z-10 mb-6">
                                    <div
                                        className={`w-24 h-24 rounded-full flex items-center justify-center font-display text-5xl font-bold shadow-md ${
                                            mode === "brand"
                                                ? "bg-coral text-white"
                                                : "bg-lime text-lime-ink"
                                        }`}
                                    >
                                        {n}
                                    </div>
                                    {idx < cfg.steps.length - 1 && (
                                        <ArrowRight className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 translate-x-full w-6 h-6 text-slate-300" />
                                    )}
                                </div>
                                <h3 className="font-display text-2xl font-semibold mb-3 text-slate-900 max-w-[260px]">
                                    {title}
                                </h3>
                                <p className="text-sm text-slate-600 leading-relaxed max-w-[260px]">
                                    {desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA — split */}
            <section className="pb-16 lg:pb-20">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-5">
                    {/* Primary CTA — 2/3 width */}
                    <div
                        className="lg:col-span-2 card-ecom p-8 sm:p-10 lg:p-12 text-white relative overflow-hidden"
                        style={{ background: "#0f172a" }}
                    >
                        <div
                            className={`absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl ${
                                mode === "brand" ? "bg-coral/30" : "bg-lime/25"
                            }`}
                        />
                        <div
                            className={`absolute -bottom-20 -left-20 w-64 h-64 rounded-full blur-3xl ${
                                mode === "brand" ? "bg-lime/20" : "bg-coral/20"
                            }`}
                        />
                        <div className="relative">
                            <div
                                className={`inline-flex mb-5 ${
                                    mode === "brand" ? "chip chip-coral" : "chip chip-lime"
                                }`}
                            >
                                JOIN CASTLINE
                            </div>
                            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-3 sm:mb-4 max-w-lg">
                                {cfg.finalH}
                            </h2>
                            <p className="text-slate-300 mb-5 sm:mb-6 text-sm max-w-md">{cfg.finalSub}</p>
                            <div className="flex flex-wrap gap-3">
                                {cfg.ctaHref.startsWith("mailto:") ? (
                                    <a href={cfg.ctaHref}>
                                        <button
                                            className="btn-pill-primary inline-flex items-center"
                                            data-testid={`final-cta-${mode}`}
                                        >
                                            {cfg.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
                                        </button>
                                    </a>
                                ) : (
                                    <Link to={cfg.ctaHref}>
                                        <button
                                            className="btn-pill-primary inline-flex items-center"
                                            data-testid={`final-cta-${mode}`}
                                        >
                                            {cfg.ctaLabel} <ArrowRight className="w-4 h-4 ml-2" />
                                        </button>
                                    </Link>
                                )}
                                <Link to="/login">
                                    <button
                                        className="btn-pill-ghost inline-flex items-center"
                                        style={{
                                            background: "transparent",
                                            color: "white",
                                            borderColor: "rgba(255,255,255,0.3)",
                                        }}
                                        data-testid="final-cta-login"
                                    >
                                        Log in
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Demo CTA — 1/3 width */}
                    <a
                        href="mailto:hello@joincastline.com?subject=Castline%20demo"
                        className="card-ecom p-7 sm:p-8 lg:p-10 surface-warm flex flex-col justify-between relative overflow-hidden hover:shadow-md transition-all group"
                        data-testid={`demo-cta-${mode}`}
                    >
                        <div>
                            <div
                                className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${
                                    mode === "brand"
                                        ? "bg-coral/10 text-coral"
                                        : "bg-lime/40 text-lime-ink"
                                }`}
                            >
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div className="overline text-slate-400 mb-2">WANT TO TALK FIRST?</div>
                            <h3 className="font-display text-2xl font-semibold mb-2 text-slate-900 leading-tight">
                                Book a 15-min demo.
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed mb-5">
                                See Castline live with one of the team. No deck, no fluff — just a walk-through of the dashboard tailored to {mode === "brand" ? "your brand" : "creator earnings"}.
                            </p>
                        </div>
                        <span className="inline-flex items-center text-sm font-semibold text-slate-900 group-hover:text-coral transition-colors">
                            Schedule a call <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" />
                        </span>
                    </a>
                </div>
            </section>

            <footer className="border-t hairline py-8">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-xs">
                            c
                        </div>
                        <span className="font-display font-semibold text-slate-900">castline</span>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs">© 2026</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                        <a
                            href="mailto:hello@joincastline.com"
                            className="text-slate-500 hover:text-coral transition-colors"
                            data-testid="footer-email"
                        >
                            hello@joincastline.com
                        </a>
                        <span className="text-slate-300">·</span>
                        <Link to="/terms" className="text-slate-500 hover:text-coral transition-colors">Terms</Link>
                        <span className="text-slate-300">·</span>
                        <Link to="/privacy" className="text-slate-500 hover:text-coral transition-colors">Privacy</Link>
                        <span className="text-slate-300">·</span>
                        <span className="flex items-center gap-1 text-slate-400">
                            <Sparkles className="w-3 h-3 text-coral" /> Make it once. Earn every time.
                        </span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
